import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import { FETCH_DEALER_FAILURE, FETCH_DEALER_SUCCESS, SELECT_DEALER } from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import { SELECT_GROUP } from 'modules/groups/actionTypes';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { getLocale } from 'modules/i18n/selectors/data';
import { isGroupManagement } from 'modules/router/selectors';
import { getDealerDefaultLanguage, getSelectedDealerId } from 'selectors/dealerSelectors';

import { clearCcmData, fetchCcmConfig, fetchCcmData, initCcmLanguage } from '../actions';
import { SAVE_CCM_DATA, SELECT_CCM_LANGUAGE } from '../actionTypes';
import { getCcmLanguage, hasCcm, isCcmLoading } from '../selectors';
import type { EntityTypeEnum } from '../types';
import { EntityTypes } from '../types';

function* getOrInitLanguage() {
  const currentLanguage: string | null = yield select(getCcmLanguage);
  if (currentLanguage) {
    return currentLanguage;
  }

  const isGroup: boolean = yield select(isGroupManagement);
  const defaultLanguage: string = isGroup
    ? yield select(getLocale)
    : yield select(getDealerDefaultLanguage);

  yield put(initCcmLanguage(defaultLanguage));
  return defaultLanguage;
}

function* refreshData(language: string) {
  const isGroup: boolean = yield select(isGroupManagement);
  const dealerId: string = yield select(getSelectedDealerId);
  const groupId: string = yield select(getSelectedGroupId);

  const entityType: EntityTypeEnum = isGroup ? EntityTypes.groups : EntityTypes.dealers;
  const id = isGroup ? groupId : dealerId;
  if (!id) {
    return;
  }

  yield put(fetchCcmConfig({ entityType, id }));
  yield put(fetchCcmData({ entityType, id, language }));
}

function* onDealerChange() {
  yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
  yield put(clearCcmData());
  const language: string = yield call(getOrInitLanguage);
  yield call(refreshData, language);
}

function* onGroupChange() {
  yield put(clearCcmData());
  const language: string = yield call(getOrInitLanguage);
  yield call(refreshData, language);
}

function* onLanguageChange() {
  const language: string = yield select(getCcmLanguage);
  yield call(refreshData, language);
}

function* onLeave() {
  yield put(clearCcmData());
}

function* onCcmPageWorker() {
  const loaded: boolean = yield select(hasCcm);
  const fetching: boolean = yield select(isCcmLoading);
  if (!loaded && !fetching) {
    const language: string = yield call(getOrInitLanguage);
    yield call(refreshData, language);
  }

  const isGroup: boolean = yield select(isGroupManagement);
  if (isGroup) {
    yield takeLatest(SELECT_GROUP, onGroupChange);
  } else {
    yield takeLatest(SELECT_DEALER, onDealerChange);
  }
  yield takeLatest(SELECT_CCM_LANGUAGE, onLanguageChange);
}

const simpleRequests = {
  [SAVE_CCM_DATA]: {
    notifications: {
      success: { id: 'ccm.save.success', defaultMessage: 'Saved successfully.' },
      failure: { id: 'ccm.save.failure', defaultMessage: 'An error occurred while saving customer communication.' },
    },
  },
};

export default function* ccmSaga() {
  yield all([
    ...simpleRequestsLoader(simpleRequests),
    scopeToPage([PATH.GROUP_CCM, PATH.DEALER_CCM], onCcmPageWorker, onLeave),
  ]);
}
